import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useDispatch } from "react-redux";
import { useReturnFieldAdminStockMutation, useGetFieldAdminStockReturnsQuery, useGetFieldAdminStockOnVanQuery } from "./fieldAdminApi";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FormPage, ScanViewport, parseDeviceQr, FAFieldRow, FAFormInput, FAFormTextarea } from "./StockFormShell";
import DesignSelect from "./DesignSelect";
import { useTeamRoster } from "./HS/InstallerRoster";
import { installerLabel, MONO_FONT_FAMILY, FONT_FAMILY, BAD_RED_TEXT, formatTimeOfDay } from "./designTokens";

// .flagged-tag.smartnode / .flagged-tag.tjommie, with the neutral .flagged-tag as the
// fallback for a model that is neither (or absent — older rows carry no model at all).
function tagTone(model) {
  const name = String(model || "").toLowerCase();
  // backgroundColor, not MUI's `bg` shorthand — `bg` is not a system prop and is dropped
  // silently, which left the tag with the right text colour on no background at all.
  if (name.includes("tjommie")) return { backgroundColor: "rgba(34,197,94,0.14)", color: "#4ADE80" };
  if (name.includes("smartnode")) return { backgroundColor: "rgba(59,130,246,0.14)", color: "#93C5FD" };
  return { backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.65)" };
}

const RETURN_REASONS = ["Faulty", "Damaged in transit", "Wrong model", "Customer refused", "Other"];

/** Returns & uninstallable.
 *
 *  Scan-only by decision (19 Aug 2026), matching the design: the serial comes from the QR
 *  and the reporter from the device's last issue, so neither is a field. Do not add a manual
 *  serial entry back without agreeing it — it was considered and declined.
 *
 *  Two known consequences, accepted:
 *    - the page cannot be driven without a camera, so it is not testable on a desktop;
 *    - a device whose QR label is damaged cannot be logged here, which is likelier on this
 *      screen than any other, since the device being returned is often the broken one.
 *
 *  The reporter picker is the one exception, and appears only when the scanned device has no
 *  issue record to derive a holder from.
 */
export default function StockReturn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deviceSerial, setDeviceSerial] = useState("");
  const [deviceImsi, setDeviceImsi] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [installerId, setInstallerId] = useState("");
  const [notes, setNotes] = useState("");

  const { installers, nameById } = useTeamRoster();
  const [tab, setTab] = useState("scan");
  const { data: returnsData } = useGetFieldAdminStockReturnsQuery();
  const flagged = returnsData?.returns || [];
  // serial -> the installer holding it. The design has no reporter input at all: a return
  // comes from whoever the device went out to, so the screen answers that itself.
  const { data: vanData } = useGetFieldAdminStockOnVanQuery();
  const issuedTo = vanData?.issued_to || {};

  // Fills the reporter, does not lock it. A device with no issue record on file — collected
  // before this trail existed, or handed over off-app — still needs a name, and the field
  // admin is standing with whoever brought it back.
  const applySerial = (serial) => {
    setDeviceSerial(serial);
    const holder = issuedTo[serial];
    if (holder) setInstallerId(holder);
  };

  const [returnStock, { isLoading, error }] = useReturnFieldAdminStockMutation();

  const handleSubmit = async () => {
    const result = await returnStock({
      device_serial: deviceSerial,
      device_imsi: deviceImsi || undefined,
      return_reason: returnReason,
      installer_id: installerId,
      notes: notes || undefined,
    });
    if (!result.error) {
      dispatch(showSuccess("Return logged"));
      // Stay put and show the pile it just joined. Leaving for the Stock page hid the one
      // confirmation that matters — that the device is now recorded as set aside.
      setDeviceSerial(""); setDeviceImsi(null);
      setReturnReason(""); setInstallerId(""); setNotes("");
      setTab("flagged");
    }
  };

  return (
    <FormPage
      eyebrow="Step 3 of stock workflow"
      title="Returns &amp; Uninstallable"
      subtitle="Faulty, damaged in transit, wrong model, refused — capture a reason and a reporter for every device that comes back."
      onSubmit={handleSubmit}
      submitLabel={isLoading ? "Saving…" : "Log return"}
      submitDisabled={isLoading || !deviceSerial || !returnReason || !installerId}
      // The design's footer reads "23000419 · SmartNode · returned by Thabo N." — with the
      // serial and reporter no longer being fields, this line is where they are confirmed
      // back to the field admin. It also names whichever requirement is still outstanding,
      // since a disabled button does not say why.
      summary={
        !deviceSerial
          ? "Scan the device coming back"
          : [
              deviceSerial,
              returnReason || null,
              installerId ? `returned by ${installerLabel(nameById.get(installerId), installerId)}` : null,
            ].filter(Boolean).join(" · ")
            + (!returnReason ? " · choose a reason" : !installerId ? " · who did it come back from?" : "")
      }
      error={error?.data?.error_message}
      hideActions={tab === "flagged"}
    >
      {/* .tab-strip — logging a return and seeing what has already come back are two
          different jobs on one screen, so they are two tabs rather than one long page. */}
      <Box sx={{
        display: "inline-flex", gap: "4px", p: "5px", alignSelf: "flex-start",
        backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "10px",
      }}>
        {[
          { key: "scan", label: "Scan return" },
          { key: "flagged", label: "Flagged returns", count: flagged.length },
        ].map((item) => {
          const active = tab === item.key;
          return (
            <Box
              key={item.key}
              component="button"
              type="button"
              onClick={() => setTab(item.key)}
              sx={{
                px: "16px", py: "8px", borderRadius: "7px", border: "none", cursor: "pointer",
                fontFamily: FONT_FAMILY, fontSize: "0.84rem", fontWeight: 600,
                backgroundColor: active ? AMBER : "transparent",
                color: active ? "#0D1117" : "rgba(255,255,255,0.6)",
                whiteSpace: "nowrap",
                "&:hover": active ? undefined : { color: "#fff" },
              }}
            >
              {item.label}
              {item.count ? (
                <Box component="span" sx={{
                  fontSize: "0.66rem", px: "6px", py: "1px", ml: "4px", borderRadius: "4px",
                  fontWeight: 700, backgroundColor: "rgba(0,0,0,0.18)",
                  color: active ? "#0D1117" : "rgba(255,255,255,0.75)",
                }}>
                  {item.count}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {tab === "flagged" ? (
        flagged.length === 0 ? (
          <Box sx={{
            p: "32px 18px", textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.88rem",
            backgroundColor: CARD_BG, border: "1px dashed rgba(255,255,255,0.08)", borderRadius: "12px",
          }}>
            Nothing flagged today. Devices you log as faulty, damaged or refused appear here.
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {/* .section-label — the tab says what you are looking at, this says what is in
                it. Without it the cards start immediately under the tabs with no scope. */}
            <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)", pt: "4px", pb: "2px" }}>
              Today's returns · {flagged.length} flagged
            </Typography>
            {flagged.map((item) => (
              <Box key={`${item.device_serial}-${item.returned_at}`} sx={{
                backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px",
                p: "14px 16px", display: "flex", alignItems: "flex-start", gap: "14px",
              }}>
                <Box sx={{
                  width: "36px", height: "36px", borderRadius: "9px", flexShrink: 0,
                  backgroundColor: "rgba(239,68,68,0.14)", color: BAD_RED_TEXT,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <WarningAmberIcon sx={{ fontSize: "1.1rem" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <Typography sx={{ fontFamily: MONO_FONT_FAMILY, fontSize: "0.9rem", color: "#fff", fontWeight: 500 }}>
                      {item.device_serial}
                    </Typography>
                    {item.device_model ? (
                      <Box component="span" sx={{
                        fontSize: "0.62rem", fontWeight: 700, px: "7px", py: "2px", borderRadius: "4px",
                        textTransform: "uppercase", letterSpacing: "0.06em",
                        ...tagTone(item.device_model),
                      }}>
                        {item.device_model}
                      </Box>
                    ) : null}
                  </Box>
                  <Typography sx={{ fontSize: "0.84rem", color: BAD_RED_TEXT, fontWeight: 600, mt: "4px" }}>
                    {item.return_reason}
                  </Typography>
                  {item.notes ? (
                    <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)", mt: "4px", lineHeight: 1.4 }}>
                      {item.notes}
                    </Typography>
                  ) : null}
                  {/* Rows written before the reporter was required carry no installer, so the
                      name is omitted rather than rendered as "Reported by —". */}
                  <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", mt: "6px", fontSize: "0.72rem", color: "rgba(255,255,255,0.4)" }}>
                    {item.installer_name ? <Box component="span">Reported by {item.installer_name}</Box> : null}
                    <Box component="span">{item.installer_name ? "· " : ""}{formatTimeOfDay(item.returned_at)}</Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )
      ) : (
      <>
      <ScanViewport
        tone="red"
        onScan={(raw) => { const { serial, imsi } = parseDeviceQr(raw); applySerial(serial); setDeviceImsi(imsi); }}
        hint="Point at the QR code · serial captured automatically"
        scannedLabel={deviceSerial ? `Scanned · ${deviceSerial}` : null}
      />

      {/* Neither the serial nor the reporter is asked for: the scan carries the serial, and
          a return comes from whoever the device was issued to, which the register already
          knows. The design has only these two inputs for the same reason. */}
      <FAFieldRow label="Reason for return" hint="The reason is recorded against the return along with your name.">
        <DesignSelect
          id="return-reason"
          value={returnReason}
          onChange={setReturnReason}
          placeholder="Choose a reason"
          options={RETURN_REASONS.map((reason) => ({ value: reason, label: reason }))}
        />
      </FAFieldRow>

      {/* Appears only when the scan cannot answer it — a device with no issue record on
          file, collected before this trail existed or handed over off-app. Asking for the
          holder every time would be asking for something already known. */}
      {deviceSerial && !issuedTo[deviceSerial] ? (
        <FAFieldRow
          label="Returned by"
          hint="This device has no issue record, so it cannot say who is bringing it back."
        >
          <DesignSelect
            id="return-reporter"
            value={installerId}
            onChange={setInstallerId}
            placeholder="Choose an installer"
            emptyText="No installers on this team's roster."
            options={installers.map((installer) => ({ value: installer.id, label: installer.name }))}
          />
        </FAFieldRow>
      ) : null}

      <FAFieldRow label="Notes (optional)" hint="Open-format notes captured against the return record." htmlFor="return-notes">
        <FAFormTextarea
          id="return-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Unit powered up briefly then died — capacitor smell. Returned with original prepack box."
        />
      </FAFieldRow>
      </>
      )}
    </FormPage>
  );
}
